import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import apiClient from "../utilities/apiClient";
import Store from "../index";
import { fetchAndStoreZatcaMetadata } from "../utilities/zatcaMetadataLoader";
import { hasZatcaApp } from "../utilities/zatcaReceiptUtils";
import { loadTaxConfiguration } from "../utilities/taxConfigurationUtils";
import {
    loadModuleConfiguration,
} from "../utilities/moduleConfigurationUtils";
import {
    isHomsAppType,
    loadModuleConfiguration as loadHomsModuleConfiguration,
    enforceZatcaByCountry,
    saveModuleConfiguration as saveHomsModuleConfiguration,
} from "../utilities/homsModuleConfigurationUtils";
import { isModuleConfigAppType } from "../utilities/appModuleConfiguration";
import { resolveCurrencyFromCountry } from "../utilities/currencyUtils";
import { ORDERS_PAGE_SIZE } from "../utilities/ordersConstants";
import { PRODUCTS_PAGE_SIZE } from "../utilities/productsConstants";
import { getFilteredProducts } from "../utilities/ProductsFilter";
import { refreshVendorsInStore } from "../utilities/vendorsRefresh";
import { refreshPurchaseRequestsInStore } from "../utilities/purchaseRequestsRefresh";
import { refreshRfqsInStore } from "../utilities/rfqsRefresh";
import { refreshPurchaseOrdersInStore } from "../utilities/purchaseOrdersRefresh";
import {
    refreshGoodsReceiptsInStore,
    refreshQualityInspectionsInStore,
    refreshPurchaseReturnsInStore,
} from "../utilities/purchaseStockRefresh";
import {
    refreshVendorBillsInStore,
    refreshVendorPaymentsInStore,
} from "../utilities/accountsPayableRefresh";
import { seedInventoryItemsFromProducts } from "../utils/inventorySync";
import { excludeHposOriginOrders } from "../utilities/orderUtils";
import {
    bootFetchOrders,
    bootFetchCustomers,
    bootFetchQuotations,
    bootFetchDeliveries,
    bootFetchInvoices,
} from "../utilities/salesBootQueries";

/**
 * Hook that handles all global data fetching on application startup.
 * Moved from ResponsiveDrawer to decouple logic from UI.
 */
const useAppDataInitialization = (props) => {
    const { authenticated, token, appType } = props;
    const dispatch = useDispatch();

    // Select necessary state from Redux
    const { companyName, instanceId, basePath, gatewayUrl } = useSelector((state) => state.globalState);
    const companyCountry = useSelector(
        (state) => state.companyInfoState?.companyInfo?.CCountry
    );
    const moduleZatca = useSelector((state) => state.moduleConfigState?.modules?.zatca);

    // Local state for chaining requests
    const [companyId, setCompanyId] = useState("");

    // Refs to prevent duplicate fetching
    const companyFetched = useRef(false);
    const purchasesFetched = useRef(false);
    const globalDataFetched = useRef(false);

    // Mount indicator
    useEffect(() => {
        console.log("💎 useAppDataInitialization Hook MOUNTED");
        return () => console.log("💾 useAppDataInitialization Hook UNMOUNTED");
    }, []);

    // 0. Company module config (localStorage → Redux) for HPRMS / HOMS
    useEffect(() => {
        if (!authenticated || !isModuleConfigAppType(appType) || !companyName || !instanceId) {
            return;
        }
        const config = isHomsAppType(appType)
            ? loadHomsModuleConfiguration(companyName, instanceId, appType)
            : loadModuleConfiguration(companyName, instanceId, appType);
        dispatch({ type: "SET_MODULE_CONFIG", payload: config });
    }, [authenticated, appType, companyName, instanceId, dispatch]);

    // 1. Fetch Company Details (gateway: /company/getCompanyInfo?userName=...)
    useEffect(() => {
        function getcompanydetail() {
            const userName = encodeURIComponent(companyName);
            const url = `/company/getCompanyInfo?userName=${userName}`;
            console.log("🏢 useAppDataInitialization: Fetching Company Info via gateway...", {
                gatewayUrl,
                url,
            });
            apiClient.get(url, { context: "Fetching Company Details", silent: true, useGateway: true })
                .then(async (resp) => {
                    if (resp) {
                        const { code: currencyCode, label: currencyLabel } =
                            resolveCurrencyFromCountry(resp.CCountry);
                        console.log("🏢 useAppDataInitialization: Company Info received", {
                            CID: resp.CID,
                            CCountry: resp.CCountry,
                            currencyCode,
                            currencyLabel,
                        });
                        await Store.dispatch({
                            type: "SET_COMPANY_INFO",
                            companyInfo: resp,
                            currencyCode,
                            currencyLabel,
                        });
                        await Store.dispatch({
                            type: "BUSSINESS_TYPE",
                            payload: { bussinessType: resp.CBusinessType },
                        });
                        await Store.dispatch({
                            type: "SET_CURRENCY_CODE",
                            payload: { currencyCode, currencyLabel },
                        });
                        setCompanyId(resp.CID);
                    }
                })
                .catch((err) => console.error("Error in getcompanydetail:", err));
        }

        if (companyName && appType && !companyFetched.current) {
            companyFetched.current = true;
            getcompanydetail();
        }
    }, [companyName, appType, gatewayUrl, instanceId, dispatch]);

    // 1b. HOMS: keep ZATCA off when country is not Saudi (covers race with module-config load)
    useEffect(() => {
        if (!authenticated || !isHomsAppType(appType) || !companyName || !instanceId) {
            return;
        }
        // Wait until company info has been fetched at least once
        if (companyCountry === undefined) return;

        const current = Store.getState().moduleConfigState;
        if (!current?.modules) return;

        const modules = enforceZatcaByCountry(current.modules, companyCountry);
        if (modules.zatca === current.modules.zatca) return;

        const next = { ...current, modules };
        saveHomsModuleConfiguration(companyName, instanceId, next, appType);
        dispatch({ type: "SET_MODULE_CONFIG", payload: next });
    }, [
        authenticated,
        appType,
        companyName,
        instanceId,
        companyCountry,
        moduleZatca,
        dispatch,
    ]);

    // 2. Fetch Company Purchases (Dependant on Company ID)
    useEffect(() => {
        function getcompanypurchases() {
            console.log("🛍️ useAppDataInitialization: Fetching Company Purchases for CID:", companyId);
            let url = `/company/getAllCompanyProducts?CID=${companyId}`;
            apiClient.get(url, { context: "Loading Data ", silent: true, useGateway: true })
                .then((resp) => {
                    console.log("🛍️ useAppDataInitialization: Purchased Apps received");
                    dispatch({ type: "GET_PURCHASED_APPS", purchasedApps: resp });

                    if (Array.isArray(resp) && hasZatcaApp(resp) && companyName) {
                        dispatch({
                            type: "TAX_CONFIGURATION",
                            tax_configuration: loadTaxConfiguration(companyName),
                        });
                        void fetchAndStoreZatcaMetadata(companyName);
                    }
                })
                .catch((err) => console.error("Error fetching company purchases:", err));
        }

        if (companyId && !purchasesFetched.current) {
            purchasesFetched.current = true;
            getcompanypurchases();
        }
    }, [companyId, companyName, dispatch]);

    // 3. Global Data Fetching (Products, Categories, etc.)
    useEffect(() => {
        // Require a real JWT string — empty [] is truthy in JS but is not a token
        const hasRealToken = Boolean(
            token &&
            !(Array.isArray(token) && token.length === 0) &&
            String(Array.isArray(token) ? token[0] : token).trim()
        );

        if (authenticated && hasRealToken && basePath && appType) {
            if (globalDataFetched.current) {
                console.log("⏭️ useAppDataInitialization: Skipping Global Data Fetch (Already Fetched)");
            } else {
                console.log(`🚀 useAppDataInitialization: Starting Global Data Initialization for: ${appType}`);
                console.log(`📍 BasePath: ${basePath}`);

                globalDataFetched.current = true;

                const fetchAllData = async () => {
                    try {
                        const essentialPromises = [];

                        // Categories & Products (inventory list is derived from products — no GET /inventory)
                        // Not needed for HCRM (customer-only) or HVMS (vendor-only)
                        if (!appType.startsWith("HCRM") && !appType.startsWith("HVMS")) {

                            // Business / Receipt Metadata
                            essentialPromises.push((async () => {
                                try {
                                    const metadata = await apiClient.get("/companyInfo/receiptMetaData", {
                                        context: "Global Init: Business Settings",
                                        silent: true,
                                    });
                                    if (metadata && typeof metadata === "object") {
                                        Store.dispatch({ type: "RECEIPT_METADATA", receipt_metadata: metadata });
                                    }
                                } catch (err) { /* silent fallback */ }
                            })());

                            // Categories
                            essentialPromises.push((async () => {
                                try {
                                    const categories = await apiClient.get("/categories", { context: "Global Init: Categories" });
                                    if (Array.isArray(categories)) Store.dispatch({ type: "GET_CATEGORIES", categories });
                                } catch (err) { console.error("Failed to fetch categories during init"); }
                            })());

                            // Products → seed active_products + inventoryItems in 2 paginated parts (up to 400 products total)
                            essentialPromises.push((async () => {
                                try {
                                    const page1 = await apiClient.get(`/products?status=All&start=0&size=${PRODUCTS_PAGE_SIZE}`, { context: "Global Init: Products (Page 1)" });
                                    let combined = Array.isArray(page1) ? [...page1] : [];

                                    // Part 2: Fetch next 200 products (start=200, size=200) to load up to 400 products in Redux
                                    if (combined.length >= PRODUCTS_PAGE_SIZE) {
                                        try {
                                            const page2 = await apiClient.get(`/products?status=All&start=${PRODUCTS_PAGE_SIZE}&size=${PRODUCTS_PAGE_SIZE}`, { context: "Global Init: Products (Page 2)", silent: true });
                                            if (Array.isArray(page2) && page2.length > 0) {
                                                const existingIds = new Set(combined.map((p) => String(p.id || p.POID || p.poid)));
                                                for (const p of page2) {
                                                    const id = String(p.id || p.POID || p.poid);
                                                    if (id && !existingIds.has(id)) {
                                                        combined.push(p);
                                                        existingIds.add(id);
                                                    }
                                                }
                                            }
                                        } catch (err2) {
                                            console.error("Failed to fetch page 2 of products during init", err2);
                                        }
                                    }

                                    if (combined.length > 0) {
                                        Store.dispatch({ type: "GET_PRODUCTS", products: combined });
                                        const active = getFilteredProducts(combined, "active");
                                        Store.dispatch({
                                            type: "GET_ACTIVE_PRODUCTS",
                                            active_products: active.length > 0 ? active : combined,
                                        });
                                        seedInventoryItemsFromProducts(
                                            active.length > 0 ? active : combined,
                                            Store.dispatch
                                        );
                                    }
                                } catch (err) { console.error("Failed to fetch products during init"); }
                            })());
                        }

                        const specificPromises = [];

                        // HPRMS (Procurement) or HRMS (Reporting)
                        if (appType.startsWith("HPRMS") || appType.startsWith("HRMS")) {
                            specificPromises.push((async () => {
                                try {
                                    await refreshVendorsInStore({
                                        context: "Global Init: Vendors",
                                        silent: true,
                                    });
                                } catch (err) { console.error("Failed to fetch vendors during init"); }
                            })());

                            specificPromises.push((async () => {
                                try {
                                    await refreshPurchaseOrdersInStore({
                                        context: "Global Init: POs",
                                        silent: true,
                                    });
                                } catch (err) { console.error("Failed to fetch POs during init"); }
                            })());
                        }

                        // Purchase Requests + RFQs + stock lists — HPRMS only (Phase A / B / D)
                        if (appType.startsWith("HPRMS")) {
                            specificPromises.push((async () => {
                                try {
                                    await refreshPurchaseRequestsInStore({
                                        context: "Global Init: Purchase Requests",
                                        silent: true,
                                    });
                                } catch (err) {
                                    console.error("Failed to fetch purchase requests during init");
                                }
                            })());
                            specificPromises.push((async () => {
                                try {
                                    await refreshRfqsInStore({
                                        context: "Global Init: RFQs",
                                        silent: true,
                                    });
                                } catch (err) {
                                    console.error("Failed to fetch RFQs during init");
                                }
                            })());
                            specificPromises.push((async () => {
                                try {
                                    await refreshGoodsReceiptsInStore({
                                        silent: true,
                                    });
                                } catch (err) {
                                    console.error("Failed to fetch goods receipts during init");
                                }
                            })());
                            specificPromises.push((async () => {
                                try {
                                    await refreshQualityInspectionsInStore({
                                        silent: true,
                                    });
                                } catch (err) {
                                    console.error("Failed to fetch quality inspections during init");
                                }
                            })());
                            specificPromises.push((async () => {
                                try {
                                    await refreshPurchaseReturnsInStore({
                                        silent: true,
                                    });
                                } catch (err) {
                                    console.error("Failed to fetch purchase returns during init");
                                }
                            })());
                            specificPromises.push((async () => {
                                try {
                                    await refreshVendorBillsInStore({
                                        silent: true,
                                        enrich: false,
                                    });
                                } catch (err) {
                                    console.error("Failed to fetch vendor bills during init");
                                }
                            })());
                            specificPromises.push((async () => {
                                try {
                                    await refreshVendorPaymentsInStore({
                                        silent: true,
                                    });
                                } catch (err) {
                                    console.error("Failed to fetch vendor payments during init");
                                }
                            })());
                        }

                        // Customers — HPOS, HOMS, HCRM, HRMS (not HVMS)
                        if (
                            appType.startsWith("HPOS") ||
                            appType.startsWith("HOMS") ||
                            appType.startsWith("HCRM") ||
                            appType.startsWith("HRMS")
                        ) {
                            specificPromises.push((async () => {
                                try {
                                    const customers = await bootFetchCustomers({
                                        hideLoader: true,
                                    });
                                    if (Array.isArray(customers)) Store.dispatch({ type: "GET_ACTIVE_CUSTOMERS", active_customers: customers });
                                } catch (err) { console.error("Failed to fetch customers during init"); }

                                try {
                                    const archivedCustomers = await bootFetchCustomers({
                                        status: "Archived",
                                        hideLoader: true,
                                    });
                                    if (Array.isArray(archivedCustomers)) {
                                        Store.dispatch({
                                            type: "GET_ARCHIVED_CUSTOMERS",
                                            archived_customers: archivedCustomers,
                                        });
                                    }
                                } catch (err) {
                                    console.error("Failed to fetch archived customers during init");
                                }
                            })());
                        }

                        // Orders — HOMS / HRMS / HPOS at boot (Sales History = Redux; Sync refetches)
                        if (
                            appType.startsWith("HOMS") ||
                            appType.startsWith("HRMS") ||
                            appType.startsWith("HPOS")
                        ) {
                            specificPromises.push((async () => {
                                try {
                                    const orders = await bootFetchOrders({
                                        start: 0,
                                        size: ORDERS_PAGE_SIZE,
                                        hideLoader: true,
                                    });
                                    if (Array.isArray(orders)) {
                                        const scoped = appType.startsWith("HOMS")
                                            ? excludeHposOriginOrders(orders)
                                            : orders;
                                        Store.dispatch({ type: "GET_ORDERS", orders: scoped });
                                        if (appType.startsWith("HPOS")) {
                                            Store.dispatch({ type: "HPOS_SALES_HYDRATED" });
                                        }
                                    }
                                } catch (err) { console.error("Failed to fetch orders during init"); }
                            })());
                        }

                        // Quotations & deliveries — HOMS industrial sales journey
                        if (appType.startsWith("HOMS")) {
                            specificPromises.push((async () => {
                                try {
                                    const quotations = await bootFetchQuotations({ hideLoader: true });
                                    if (Array.isArray(quotations)) {
                                        Store.dispatch({ type: "SET_QUOTATIONS", payload: quotations });
                                    }
                                } catch (err) { console.error("Failed to fetch quotations during init"); }
                            })());

                            specificPromises.push((async () => {
                                try {
                                    const deliveries = await bootFetchDeliveries({ hideLoader: true });
                                    if (Array.isArray(deliveries)) {
                                        Store.dispatch({ type: "SET_DELIVERIES", payload: deliveries });
                                    }
                                } catch (err) { console.error("Failed to fetch deliveries during init"); }
                            })());

                            specificPromises.push((async () => {
                                try {
                                    const invoices = await bootFetchInvoices({ hideLoader: true });
                                    if (Array.isArray(invoices)) {
                                        Store.dispatch({ type: "SET_INVOICES", payload: invoices });
                                    }
                                } catch (err) { console.error("Failed to fetch invoices during init"); }
                            })());
                        }

                        await Promise.all([...essentialPromises, ...specificPromises]);
                        console.log("🏁 useAppDataInitialization: Global Data Initialization Complete");
                    } catch (error) {
                        console.error("🏁 useAppDataInitialization: Error during Global Data Initialization:", error);
                    }
                };

                fetchAllData();
            }
        } else {
            const missing = [];
            if (!authenticated) missing.push("authenticated");
            if (!hasRealToken) missing.push("token");
            if (!basePath) missing.push("basePath");
            if (!appType) missing.push("appType");
            if (missing.length > 0 && missing.length < 4) {
                console.log(`⏳ useAppDataInitialization: Waiting for ${missing.join(", ")}`);
            }
        }
    }, [authenticated, token, basePath, appType]);
};

export default useAppDataInitialization;